import type { PDFForm } from 'pdf-lib'
import { ABILITIES, SKILLS, SKILL_KEYS } from '@shared/rules/constants'
import {
  ABILITY_FIELDS, PAGE1, PAGE2, SAVE_FIELDS, SHEET_PDF_URL, SKILL_FIELDS,
  SPELL_ROWS, SPELL_SLOT_FIELDS, WEAPON_ROWS,
} from './fieldMap'
import type { SheetData } from './sheetData'

const signed = (value: number): string => (value < 0 ? `${value}` : `+${value}`)

/** Fields are auto-named; a rename upstream should not throw away the export. */
function setText(form: PDFForm, name: string, value: string, fontSize?: number): void {
  try {
    const field = form.getTextField(name)
    if (fontSize) field.setFontSize(fontSize)
    field.setText(value)
  } catch {
    // Field missing from this revision of the sheet — skip it.
  }
}

function setMultiline(form: PDFForm, name: string, value: string, fontSize = 8): void {
  try {
    const field = form.getTextField(name)
    field.enableMultiline()
    field.setFontSize(fontSize)
    field.setText(value)
  } catch {
    /* ignore */
  }
}

function setCheck(form: PDFForm, name: string, checked: boolean): void {
  try {
    const box = form.getCheckBox(name)
    if (checked) box.check()
    else box.uncheck()
  } catch {
    /* ignore */
  }
}

const feature = (entry: { name: string; text: string }): string => `${entry.name}. ${entry.text}`

/**
 * Splits feature text across the sheet's two Class Features columns at a
 * whole-feature boundary, keeping roughly even column lengths.
 */
function splitColumns(entries: string[], budget = 620): [string, string] {
  const left: string[] = []
  const right: string[] = []
  let used = 0

  for (const entry of entries) {
    if (used + entry.length <= budget || left.length === 0) {
      left.push(entry)
      used += entry.length + 2
    } else {
      right.push(entry)
    }
  }
  return [left.join('\n\n'), right.join('\n\n')]
}

export async function buildCharacterSheetPdf(data: SheetData): Promise<Uint8Array> {
  const response = await fetch(SHEET_PDF_URL)
  if (!response.ok) {
    throw new Error('Could not load the character sheet template')
  }
  const template = await response.arrayBuffer()
  return fillSheet(template, data)
}

export async function fillSheet(template: ArrayBuffer, data: SheetData): Promise<Uint8Array> {
  // pdf-lib is ~500 kB, and most sessions never export. Pull it in on demand.
  const { PDFDocument, StandardFonts } = await import('pdf-lib')

  const doc = await PDFDocument.load(template)
  const form = doc.getForm()
  const helvetica = await doc.embedFont(StandardFonts.Helvetica)

  // ---------- Header ----------
  setText(form, PAGE1.name, data.name)
  setText(form, PAGE1.background, data.background)
  setText(form, PAGE1.className, data.className)
  setText(form, PAGE1.species, data.species)
  setText(form, PAGE1.subclass, data.subclass)
  setText(form, PAGE1.level, String(data.level))
  setText(form, PAGE1.xp, String(data.xp))

  setText(form, PAGE1.armorClass, String(data.armorClass))
  setCheck(form, PAGE1.shield, data.shieldEquipped)

  setText(form, PAGE1.hpCurrent, String(data.hpCurrent))
  setText(form, PAGE1.hpMax, String(data.hpMax))
  setText(form, PAGE1.hpTemp, data.hpTemp > 0 ? String(data.hpTemp) : '')
  setText(form, PAGE1.hitDiceMax, data.hitDiceMax)
  setText(form, PAGE1.hitDiceSpent, String(data.hitDiceSpent))

  setText(form, PAGE1.proficiencyBonus, signed(data.proficiencyBonus))
  setText(form, PAGE1.initiative, signed(data.initiative))
  // The Speed box is narrow and auto-sizes large; pin it so "30 ft, fly 30" fits.
  setText(form, PAGE1.speed, data.speed, 10)
  setText(form, PAGE1.size, data.size)
  setText(form, PAGE1.passivePerception, String(data.passivePerception))
  setCheck(form, PAGE1.heroicInspiration, data.heroicInspiration)

  // ---------- Abilities, saves, skills ----------
  for (const ability of ABILITIES) {
    const [modifierField, scoreField] = ABILITY_FIELDS[ability]
    setText(form, modifierField, signed(data.modifiers[ability]))
    setText(form, scoreField, String(data.scores[ability]))

    const [saveBox, saveField] = SAVE_FIELDS[ability]
    setCheck(form, saveBox, data.saveProficiencies.includes(ability))
    setText(form, saveField, signed(data.saveBonuses[ability]))
  }

  for (const skill of SKILL_KEYS) {
    const [box, field] = SKILL_FIELDS[skill]
    setCheck(form, box, Boolean(data.skillProficiencies[skill]))
    setText(form, field, signed(data.skillBonuses[skill]))
  }

  // ---------- Weapons ----------
  data.attacks.slice(0, WEAPON_ROWS.length).forEach((attack, index) => {
    const row = WEAPON_ROWS[index]!
    setText(form, row[0], attack.name)
    setText(form, row[1], attack.bonus)
    setText(form, row[2], attack.damage, 8)
    setText(form, row[3], attack.notes, 7)
  })

  // ---------- Feature boxes ----------
  const [featuresLeft, featuresRight] = splitColumns(data.classFeatures.map(feature))
  setMultiline(form, PAGE1.classFeaturesLeft, featuresLeft)
  setMultiline(form, PAGE1.classFeaturesRight, featuresRight)
  setMultiline(form, PAGE1.speciesTraits, data.speciesTraits.map(feature).join('\n\n'))
  setMultiline(form, PAGE1.feats, data.feats.map(feature).join('\n\n'))

  setMultiline(form, PAGE1.weaponProficiencies, data.weaponTraining, 8)
  setMultiline(form, PAGE1.tools, data.tools.join(', '), 8)

  setCheck(form, PAGE1.armorTraining.light, data.armorTraining.light)
  setCheck(form, PAGE1.armorTraining.medium, data.armorTraining.medium)
  setCheck(form, PAGE1.armorTraining.heavy, data.armorTraining.heavy)
  setCheck(form, PAGE1.armorTraining.shields, data.armorTraining.shields)

  // ---------- Page 2 ----------
  setMultiline(form, PAGE2.appearance, data.appearance, 9)
  setMultiline(form, PAGE2.backstory, data.backstory, 9)
  setText(form, PAGE2.alignment, data.alignment)
  setMultiline(form, PAGE2.languages, data.languages.join(', '), 9)
  setMultiline(form, PAGE2.equipment, data.equipment.join('\n'), 8)

  setText(form, PAGE2.coins.cp, data.coins.cp ? String(data.coins.cp) : '')
  setText(form, PAGE2.coins.sp, data.coins.sp ? String(data.coins.sp) : '')
  setText(form, PAGE2.coins.ep, data.coins.ep ? String(data.coins.ep) : '')
  setText(form, PAGE2.coins.gp, data.coins.gp ? String(data.coins.gp) : '')
  setText(form, PAGE2.coins.pp, data.coins.pp ? String(data.coins.pp) : '')

  if (data.spellcasting) {
    const sc = data.spellcasting
    setText(form, PAGE2.spellcastingAbility, sc.abilityName)
    setText(form, PAGE2.spellcastingModifier, signed(sc.modifier))
    setText(form, PAGE2.spellSaveDC, String(sc.saveDC))
    setText(form, PAGE2.spellAttackBonus, signed(sc.attackBonus))

    for (const [level, field] of Object.entries(SPELL_SLOT_FIELDS)) {
      const count = sc.slots[Number(level)]
      setText(form, field, count ? String(count) : '')
    }

    sc.spells.slice(0, SPELL_ROWS.length).forEach((spell, index) => {
      const row = SPELL_ROWS[index]!
      setText(form, row.level, spell.level === 0 ? 'C' : String(spell.level))
      setText(form, row.name, spell.name)
      setText(form, row.castingTime, spell.castingTime, 7)
      setText(form, row.range, spell.range, 7)
      setCheck(form, row.concentration, spell.concentration)
      setCheck(form, row.ritual, spell.ritual)
      setCheck(form, row.material, spell.material)
      setText(form, row.notes, spell.notes, 6)
    })
  }

  // Regenerate appearances so the values render before anyone clicks a field.
  form.updateFieldAppearances(helvetica)

  doc.setTitle(`${data.name} — Level ${data.level} ${data.className}`)
  doc.setCreator('Campaign Hub')
  doc.setProducer('Campaign Hub')

  return doc.save()
}

/** Trigger a browser download of the filled sheet. */
export async function downloadCharacterSheet(data: SheetData): Promise<void> {
  const bytes = await buildCharacterSheetPdf(data)
  const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = `${slug(data.name || 'character')}-character-sheet.pdf`
  document.body.appendChild(link)
  link.click()
  link.remove()

  // Revoke on the next tick so Safari has finished handing the blob to the OS.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const slug = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'character'

export { SKILLS }
